import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useDocuments, useMailAccounts } from '../api/hooks'
import { DOC_TYPES, SOURCE_LABELS } from '../api/models'
import {
  Badge,
  EmptyState,
  ErrorState,
  LoadingBlock,
  formatAmount,
  formatDate,
} from '../components/ui'

export function DocumentsPage() {
  const [filters, setFilters] = useState<{ doc_type?: string; source?: string; account_id?: string }>(
    {},
  )
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const accounts = useMailAccounts()
  const query = useDocuments({ ...filters, cursor, limit: 30 })

  const update = (patch: Partial<typeof filters>) => {
    setCursor(undefined)
    setFilters((current) => {
      const next = { ...current, ...patch }
      for (const key of Object.keys(next) as (keyof typeof next)[]) {
        if (!next[key]) delete next[key]
      }
      return next
    })
  }

  return (
    <>
      <h1 className="mb-6 text-2xl">Dokumente</h1>

      <form
        className="card mb-4 grid gap-3 sm:grid-cols-3"
        aria-label="Filter"
        onSubmit={(event) => event.preventDefault()}
      >
        <div>
          <label className="label" htmlFor="filter-type">
            Typ
          </label>
          <select
            id="filter-type"
            className="input"
            value={filters.doc_type ?? ''}
            onChange={(event) => update({ doc_type: event.target.value })}
          >
            <option value="">Alle</option>
            {DOC_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="filter-source">
            Quelle
          </label>
          <select
            id="filter-source"
            className="input"
            value={filters.source ?? ''}
            onChange={(event) => update({ source: event.target.value })}
          >
            <option value="">Alle</option>
            <option value="mail">E-Mail</option>
            <option value="letter">Brief</option>
            <option value="attachment">Anhang</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="filter-account">
            Konto
          </label>
          <select
            id="filter-account"
            className="input"
            value={filters.account_id ?? ''}
            onChange={(event) => update({ account_id: event.target.value })}
          >
            <option value="">Alle</option>
            {(accounts.data ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>
      </form>

      {query.isPending ? (
        <LoadingBlock label="Dokumente werden geladen" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyState
          title="Keine Dokumente"
          description="Für diese Filter gibt es nichts. Filter zurücksetzen oder einen Brief hochladen."
          action={
            <button type="button" className="btn-secondary mt-2" onClick={() => update({ doc_type: '', source: '', account_id: '' })}>
              Filter zurücksetzen
            </button>
          }
        />
      ) : (
        <>
          <p className="mb-2 text-sm text-paper-500" role="status">
            {query.data.total} Dokumente insgesamt
          </p>
          <ul className="space-y-2">
            {query.data.items.map((document) => (
              <li key={document.id} className="card">
                <div className="flex flex-wrap items-start gap-2">
                  <Link
                    to="/dokumente/$documentId"
                    params={{ documentId: document.id }}
                    className="min-w-0 flex-1 font-medium hover:underline"
                  >
                    {document.title || 'Ohne Betreff'}
                  </Link>
                  {document.doc_type ? <Badge tone="accent">{document.doc_type}</Badge> : null}
                  <Badge>{SOURCE_LABELS[document.source] ?? document.source}</Badge>
                  {document.action_required ? <Badge tone="warn">Aktion nötig</Badge> : null}
                  {document.user_corrected ? <Badge tone="ok">korrigiert</Badge> : null}
                </div>
                {document.summary ? (
                  <p className="mt-1 max-w-prose text-sm text-paper-600 dark:text-paper-300">
                    {document.summary}
                  </p>
                ) : null}
                <p className="mt-1 text-xs text-paper-500">
                  {formatDate(document.document_date ?? document.created_at)}
                  {document.amount_total !== null && document.amount_total !== undefined
                    ? ` · ${formatAmount(document.amount_total, document.currency)}`
                    : ''}
                  {document.due_date ? ` · fällig ${formatDate(document.due_date)}` : ''}
                </p>
              </li>
            ))}
          </ul>

          {query.data.next_cursor ? (
            <button
              type="button"
              className="btn-secondary mt-4"
              onClick={() => setCursor(query.data.next_cursor ?? undefined)}
            >
              Mehr laden
            </button>
          ) : null}
        </>
      )}
    </>
  )
}
